import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView, Decoration } from '@codemirror/view';
import { StateField, StateEffect } from '@codemirror/state';
import { Button } from './ui/button';
import { Play, RotateCcw, Copy, Check, Loader2, Bug } from 'lucide-react';
import { useState } from 'react';

// --- CodeMirror debug-line decoration (defined once, outside the component) ---
const setDebugLineEffect = StateEffect.define();

const debugLineDeco = Decoration.line({ class: 'cm-debug-line' });
const debugGutterDeco = Decoration.line({ class: 'cm-debug-line-gutter' });

const debugLineField = StateField.define({
  create() {
    return Decoration.none;
  },
  update(decos, tr) {
    // Check if any of our effects are in this transaction
    for (const effect of tr.effects) {
      if (effect.is(setDebugLineEffect)) {
        const lineNumber = effect.value; // 1-indexed, or 0/null to clear
        if (!lineNumber || lineNumber <= 0) {
          return Decoration.none;
        }
        // Validate the line number is within the document
        if (lineNumber > tr.state.doc.lines) {
          return Decoration.none;
        }
        const line = tr.state.doc.line(lineNumber);
        return Decoration.set([
          debugLineDeco.range(line.from),
          debugGutterDeco.range(line.from),
        ]);
      }
    }
    // If the document changed, map decorations through the changes
    return decos.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

const defaultCode = `# ברוכים הבאים ל-PyZone! 🐍
# Welcome to PyZone!

# בואו נתחיל עם תוכנית פשוטה
name = "עולם"
print(f"שלום {name}!")

# לולאה שמדפיסה מספרים
for i in range(5):
    print(f"מספר {i}")
`;

export const EditorPane = ({ 
  code, 
  onCodeChange, 
  onRun, 
  isRunning = false,
  onClear,
  currentLine = 0,
}) => {
  const [copied, setCopied] = useState(false);
  const editorRef = useRef(null);

  // Custom theme extension
  const customTheme = useMemo(() => EditorView.theme({
    '&': {
      fontSize: '14px',
      fontFamily: "'JetBrains Mono', monospace",
    },
    '.cm-content': {
      fontFamily: "'JetBrains Mono', monospace",
    },
    '.cm-gutters': {
      backgroundColor: 'hsl(var(--editor-gutter))',
      borderRight: '1px solid hsl(var(--border) / 0.2)',
    },
    '.cm-activeLineGutter': {
      backgroundColor: 'hsl(var(--editor-line) / 0.2)',
    },
    '.cm-activeLine': {
      backgroundColor: 'hsl(var(--editor-line) / 0.08)',
    },
    // Debug line highlight
    '.cm-debug-line': {
      backgroundColor: 'hsl(var(--warning) / 0.3) !important',
      borderLeft: '3px solid hsl(var(--warning)) !important',
    },
    '.cm-debug-line-gutter': {
      backgroundColor: 'hsl(var(--warning) / 0.4) !important',
      color: 'hsl(var(--warning-foreground)) !important',
    },
  }), []);

  const extensions = useMemo(() => [
    python(),
    customTheme,
    EditorView.lineWrapping,
    debugLineField,
  ], [customTheme]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code || defaultCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onRun?.();
    }
  }, [onRun]);

  // Ref to the CodeMirror EditorView instance
  const viewRef = useRef(null);

  // Dispatch debug-line decoration whenever currentLine changes
  useEffect(() => {
    const view = viewRef.current;
    if (view) {
      view.dispatch({
        effects: setDebugLineEffect.of(currentLine),
      });
    }
  }, [currentLine]);

  return (
    <div className="flex flex-col h-full bg-editor-bg rounded-xl overflow-hidden border border-border/20">
      {/* Action Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-editor-gutter border-b border-border/20">
        <div className="flex items-center gap-3">
          {/* Window dots */}
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <span className="text-muted-foreground text-sm font-mono">main.py</span>
          {currentLine > 0 && (
            <span className="text-warning text-xs font-medium px-2 py-0.5 bg-warning/20 rounded-full flex items-center gap-1">
              <Bug className="h-3 w-3" />
              שורה {currentLine}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Copy button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/20"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>

          {/* Clear button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/20"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>

          {/* Run button */}
          <Button
            onClick={onRun}
            disabled={isRunning}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-6 rounded-full shadow-glow btn-glow transition-all duration-200"
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 ml-2 animate-spin rtl-flip" />
                <span>רץ...</span>
              </>
            ) : (
              <>
                <Play className="h-4 w-4 ml-2 fill-current rtl-flip" />
                <span>הרץ</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Code Editor */}
      <div 
        className="flex-1 overflow-auto editor-ltr relative"
        onKeyDown={handleKeyDown}
        ref={editorRef}
      >
        <CodeMirror
          value={code || defaultCode}
          height="100%"
          theme={oneDark}
          extensions={extensions}
          onChange={onCodeChange}
          onCreateEditor={(view) => {
            viewRef.current = view;
            // Apply initial debug line if already set
            if (currentLine > 0) {
              view.dispatch({
                effects: setDebugLineEffect.of(currentLine),
              });
            }
          }}
          basicSetup={{
            lineNumbers: true,
            highlightActiveLineGutter: true,
            highlightActiveLine: !currentLine, // Disable when debugging
            foldGutter: true,
            dropCursor: true,
            allowMultipleSelections: true,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            rectangularSelection: true,
            crosshairCursor: false,
            highlightSelectionMatches: true,
            tabSize: 4,
          }}
          className="h-full text-sm"
        />
      </div>

      {/* Keyboard shortcut hint */}
      <div className="px-4 py-2 bg-editor-gutter border-t border-border/20 flex justify-between items-center">
        <span className="text-xs text-muted-foreground">
          {currentLine > 0 ? '🔍 מצב דיבוג פעיל' : ''}
        </span>
        <span className="text-xs text-muted-foreground">
          Ctrl + Enter להרצה מהירה
        </span>
      </div>
    </div>
  );
};
